import { BrowserModule } from '@angular/platform-browser';
import {
  ApplicationInitStatus,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  Injector,
  NgModule,
  DOCUMENT,
} from '@angular/core';

import { PresentationComponent } from './views/presentation/presentation.component';
import { SharedModule } from './configuration/shared.module';
import { HeaderBarComponent } from './controls/header-bar/header-bar.component';
import { SidebarComponent } from './controls/sidebar/sidebar.component';
import { environment } from './configuration/get-environment.util';
import { Environment, ENVIRONMENT } from './models/environment.type';
import { PluginResolverServiceImpl } from './services/plugin-resolver.service';
import { RouterModule } from '@angular/router';
import { TOKENS } from '@trustedshops/tswp-core-authorization';
import { AngularKeycloakHttpInterceptor } from '@trustedshops/tswp-core-authorization-keycloak-implementation';
import { forcedLastInListRoutes } from './configuration/routing.bootstrapper';
import { OrganizationalContainerSelectorComponent } from './controls/organizational-container-selector/organizational-container-selector.component';
import { WebComponentWrapperComponent } from './controls/web-component-wrapper/web-component-wrapper.component';
import { OrganizationalContainerComponent } from './controls/organizational-container/organizational-container.component';
import { ClickedOutsideDirective } from './directive/clicked-outside.directive';
import { LogLevel } from '@trustedshops/tswp-core-common';
import { TertiaryMenuBarComponentInitializer } from './controls/tertiary-menu-bar/tertiary-menu-bar.bootstrapper';
import { TertiaryMenuBarComponent } from './controls/tertiary-menu-bar/tertiary-menu-bar.component';
import { SettingsInitializer } from './configuration/settings.bootstrapper';
import { ChannelManagementInitializer } from './controls/channel-management/channel-management.bootstrapper';
import { ChannelManagementComponent } from './controls/channel-management/channel-management.component';
import { NavigationBarService } from './services/navigation-bar.service';
import { AsObservablePipe } from './pipes/as-observable.pipe';
import { NavigationBadgeComponent } from './controls/navigation-badge/navigation-badge.component';
import { GoogleAnalyticsTrackingInitializer } from './configuration/ga-tracking.bootstrapper';
import { TrackingConfiguration } from './configuration/tracking.configuration';
import { UserCentricsInitializer } from './configuration/user-centrics.bootstrapper';

import { WebComponentsInitializer } from './configuration/web-components.bootstrapper';
import { ApplicationInitStatusEx } from './diagnostics/application-init-status';
import { LocaleSyncInitializer } from './configuration/locale-sync.bootstrapper';
import { RemoveHeliosPrefixPipe } from './pipes/remove-helios-prefix.pipe';
import { FeedbackSleeveComponent } from './controls/feedback-sleeve/feedback-sleeve.component';
import { FeedbackControlComponent } from './controls/feedback-control/feedback-control.component';
import { FeedbackModalComponent } from './controls/feedback-modal/feedback-modal.component';
import { FeedbackService } from './services/feedback.service';
import { FeedbackPopupComponent } from './controls/feedback-popup/feedback-popup.component';
import { FeedbackDataService } from './services/feedback-data.service';
import { PageTitleBootstrapperInitializer } from './configuration/page-title.bootstrapper';
import { ReviewsService } from './services/reviews.service';
import {
  ProductFeatureUpgradeBannerModule,
  ProductFeatureUpgradeButtonModule,
  ProductFeatureUpgradePageModule,
  UpgradePageModule,
  UpgradePlusModule,
  UpgradePopupModule,
  HubspotModule,
} from '@trustedshops/tswp-core-ui-implementation';
import { UpgradeButtonComponent } from './controls/upgrade-button/upgrade-button.component';
import { HubSpotInitializer } from './configuration/hubspot.bootstrapper';
import { MsClarityInitializer } from './configuration/ms-clarity.bootstrapper';
import { B2BModalComponent } from './controls/b2b-modal/b2b-modal.component';
import { UserflowTriggerComponent } from './controls/userflow-trigger/userflow-trigger.component';
import { UserOnboardingInitService } from './services/user-onboarding-init.service';
import { AccountService } from './services/account.service';
import { ChannelsService } from './services/channels.service';
import { UserFlowAssociationService } from './services/user-flow-association.service';
import { UserFlowProgressService } from './services/user-flow-progress.service';
import { AngularHttpClientModule } from './angular-http-client.module';
import { OnboardingBootstrapperInitializer } from './configuration/onboarding.bootstrapper';
import { UserFlowPermissionsService } from './services/user-flow-permissions.service';
import { UserService } from './services/user.service';
import { ChatbotService } from './services/chatbot.service';
import { UpgradeCenterCtaComponent } from './controls/upgrade-center-cta/upgrade-center-cta.component';
import { CompanyService } from './services/company.service';
import { KameleoonInitializer } from './configuration/kameleeon.bootstrapper';

declare let platformConfiguration: Environment;

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  declarations: [
    ClickedOutsideDirective,

    PresentationComponent,
    HeaderBarComponent,
    SidebarComponent,
    TertiaryMenuBarComponent,
    OrganizationalContainerSelectorComponent,
    OrganizationalContainerComponent,
    ChannelManagementComponent,
    WebComponentWrapperComponent,
    NavigationBadgeComponent,
    FeedbackControlComponent,
    FeedbackPopupComponent,
    FeedbackSleeveComponent,
    FeedbackModalComponent,
    B2BModalComponent,

    AsObservablePipe,
    RemoveHeliosPrefixPipe,
    UpgradeButtonComponent,
    UpgradeCenterCtaComponent,
    UserflowTriggerComponent,
  ],
  imports: [
    BrowserModule,
    RouterModule.forRoot([...forcedLastInListRoutes], {
      enableTracing: (
        platformConfiguration?.diagnostics?.logging?.logLevels || []
      ).includes(LogLevel.Trace),
    }),
    SharedModule,
    HubspotModule.forRoot({
      baseUrl: environment.freeAccount.hubspot.upgradePage.baseUrl,
      authToken: environment.freeAccount.hubspot.upgradePage.authToken,
      portalId: environment.freeAccount.hubspot.upgradePage.portalId,
      fullContactFormId:
        environment.freeAccount.hubspot.upgradePage.fullContactFormId,
      shortContactFormId:
        environment.freeAccount.hubspot.upgradePage.shortContactFormId,
      ctaContactFormId:
        environment.freeAccount.hubspot.upgradePage.ctaContactFormId,
    }),
    AngularHttpClientModule,
    ProductFeatureUpgradePageModule,
    ProductFeatureUpgradeBannerModule,
    ProductFeatureUpgradeButtonModule.forRoot({
      comparePlansUrl: environment.upgradeJourney.comparePlansUrl,
    }),
  ],
  exports: [
    BrowserModule,
    RouterModule,
    SharedModule,
    UpgradePageModule,
    UpgradePopupModule,
    UpgradePlusModule,
    ProductFeatureUpgradePageModule,
    ProductFeatureUpgradeBannerModule,
    ProductFeatureUpgradeButtonModule,
  ],
  providers: [
    PageTitleBootstrapperInitializer,
    { provide: ApplicationInitStatus, useClass: ApplicationInitStatusEx },
    ...TrackingConfiguration,
    UserCentricsInitializer,
    NavigationBarService,
    GoogleAnalyticsTrackingInitializer,
    OnboardingBootstrapperInitializer,
    TertiaryMenuBarComponentInitializer,
    HubSpotInitializer,
    MsClarityInitializer,
    WebComponentsInitializer,
    // SettingsInitializer must stay listed before ChannelManagementInitializer
    // for human readability, but array position is NOT what enforces the
    // ordering: multi-provider APP_INITIALIZERs run concurrently via
    // Promise.all. The real guarantee is ChannelManagementBootstrapper's own
    // `await this._settingsBootstrapper.initialized` — see its constructor.
    SettingsInitializer,
    ChannelManagementInitializer,
    LocaleSyncInitializer,
    KameleoonInitializer,
    PluginResolverServiceImpl,
    {
      provide: TOKENS.NgHttpInterceptor,
      useClass: AngularKeycloakHttpInterceptor,
    },
    { provide: ENVIRONMENT, useFactory: () => environment },
    FeedbackService,
    FeedbackDataService,
    ReviewsService,
    UserOnboardingInitService,
    UserService,
    AccountService,
    ChannelsService,
    UserFlowAssociationService,
    UserFlowProgressService,
    UserFlowPermissionsService,
    ChatbotService,
    CompanyService,
  ],
})
export class AppModule {
  private readonly _injector: Injector;
  private readonly _document: Document;

  /**
   * Creates a new instance of DefaultOrganizationalContainerRendererModule
   *
   * @param injector The injector to use for instantiating the controls
   * @param document The document object to register the WebComponents at
   */
  public constructor(injector: Injector, @Inject(DOCUMENT) document: Document) {
    this._injector = injector;
    this._document = document;

    if (localStorage.getItem('d3bug')) {
      const appInitStatus = injector.get(ApplicationInitStatus);

      this.checkInits(appInitStatus as ApplicationInitStatusEx);
      (window as any).tswp = (window as any).tswp || {};
      (window as any).tswp.injector = injector;
    }
  }

  private async checkInits(appInits: ApplicationInitStatusEx): Promise<void> {
    while (!appInits.done) {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!appInits.asyncInitPromises) {
        continue;
      }
      const mappings = appInits.asyncInitPromises.map((promise, i) => ({
        initFunction: (appInits as any).appInits[i],
        promise: promise as any,
      }));

      const openInits = mappings
        .filter((x) => !x.promise.__zone_symbol__state)
        .map((x) => x.initFunction);

      console.log('Still awaiting inits of', openInits);
    }
  }
  /**
   * Initializes this module and all associated custom elements
   */
}
